import { z } from 'zod';

export const formCodeDtoSchema = z.object({
  id: z.string().nullable(),
  code: z.string().nullable(),
  displayName: z.string().nullable()
});
export type FormCodeDto = z.infer<typeof formCodeDtoSchema>;

export const createOrderDtoSchema = z.object({
  orderId: z.string().nullable(),
  frequency: z.string().nullable(),
  administrationMethod: z.string().nullable(),
  route: z.string().nullable(),
  durationDays: z.number().nullable()
});
export type CreateOrderDto = z.infer<typeof createOrderDtoSchema>;

export const alertDtoSchema = z.object({
  textStickerId: z.string().nullable(),
  tooltipContent: z.string().nullable()
});
export type AlertDto = z.infer<typeof alertDtoSchema>;

export const notificationDtoSchema = z.object({
  targetUserId: z.string().nullable(),
  method: z.string().nullable(),
  content: z.string().nullable()
});
export type NotificationDto = z.infer<typeof notificationDtoSchema>;

export const autoGenerateFormDtoSchema = z.object({
  generatedTemplateId: z.string().nullable(),
  durationDays: z.number().nullable()
});
export type AutoGenerateFormDto = z.infer<typeof autoGenerateFormDtoSchema>;

export const componentSimpleForCategoryResSchema = z.object({
  id: z.string().nullable(),
  formComponentName: z.string().nullable(),
  formComponentCategoryId: z.string().nullable(),
  sort: z.number().nullable(),
  lockVersion: z.number().nullable()
});
export type ComponentSimpleForCategoryRes = z.infer<typeof componentSimpleForCategoryResSchema>;

export const actionsSchema = z.object({
  execute: z.boolean().nullable(),
  view: z.boolean().nullable(),
  print: z.boolean().nullable(),
  download: z.boolean().nullable(),
  cancel: z.boolean().nullable()
});
export type Actions = z.infer<typeof actionsSchema>;

export const cancelSchema = z.object({
  reason: z.string().nullable(),
  cancelUserName: z.string().nullable(),
  cancelDatetime: z.string().nullable()
});
export type Cancel = z.infer<typeof cancelSchema>;

export const orderSchema = z.object({
  encounterOrderId: z.string().nullable(),
  orderCode: z.string().nullable(),
  orderName: z.string().nullable()
});
export type Order = z.infer<typeof orderSchema>;

export const signatureSchema = z.object({
  completed: z.number().nullable(),
  total: z.number().nullable()
});
export type Signature = z.infer<typeof signatureSchema>;

export const queryFormByEventIdResSchema = z.object({
  id: z.string().nullable(),
  templateId: z.string().nullable(),
  templateCode: z.string().nullable(),
  name: z.string().nullable(),
  category: formCodeDtoSchema.nullable(),
  type: formCodeDtoSchema.nullable(),
  specialty: formCodeDtoSchema.nullable(),
  lockVersion: z.number().nullable(),
  disabled: z.boolean().nullable()
});
export type QueryFormByEventIdRes = z.infer<typeof queryFormByEventIdResSchema>;

export const formTemplateDtoSchema = z.object({
  modifyDatetime: z.string().nullable(),
  changeVersion: z.number().nullable()
});
export type FormTemplateDto = z.infer<typeof formTemplateDtoSchema>;

export const workFlowDtoSchema = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
  conditions: z.array(z.unknown()).nullable(),
  autoGenerateForms: z.array(autoGenerateFormDtoSchema).nullable(),
  notifications: z.array(notificationDtoSchema).nullable(),
  alerts: z.array(alertDtoSchema).nullable(),
  createOrders: z.array(createOrderDtoSchema).nullable(),
  lockVersion: z.number().nullable()
});
export type WorkFlowDto = z.infer<typeof workFlowDtoSchema>;

export const leaseLockPayloadDtoSchema = z.object({
  ownerId: z.string().nullable(),
  token: z.string().nullable()
});
export type LeaseLockPayloadDto = z.infer<typeof leaseLockPayloadDtoSchema>;

export const componentCategoryResSchema = z.object({
  id: z.string().nullable(),
  categoryName: z.string().nullable(),
  parentId: z.string().nullable(),
  sort: z.number().nullable(),
  lockVersion: z.number().nullable(),
  subCategoryList: z.unknown().nullable(),
  componentList: z.array(componentSimpleForCategoryResSchema).nullable()
});
export type ComponentCategoryRes = z.infer<typeof componentCategoryResSchema>;

export const componentResSchema = z.object({
  recordStatus: z.enum(["C", "U", "D", "R"]).nullable(),
  id: z.string().nullable(),
  createDatetime: z.string().nullable(),
  createUser: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  modifyUser: z.string().nullable(),
  lockVersion: z.number().nullable(),
  formComponentName: z.string().nullable(),
  formComponentCategoryId: z.string().nullable(),
  componentContent: z.string().nullable(),
  sort: z.number().nullable(),
  createUserName: z.string().nullable(),
  modifyUserName: z.string().nullable()
});
export type ComponentRes = z.infer<typeof componentResSchema>;

export const queryPatientEducationResSchema = z.object({
  educationId: z.string().nullable(),
  rowType: z.enum(["TODO", "FORM"]).nullable(),
  templateId: z.string().nullable(),
  formCode: z.string().nullable(),
  name: z.string().nullable(),
  status: z.enum(["pending", "draft", "completed", "pendingReview", "pendingSignature", "pendingReviewAndSignature", "signed", "cancelled"]).nullable(),
  signature: signatureSchema.nullable(),
  createDatetime: z.string().nullable(),
  createUserName: z.string().nullable(),
  source: z.enum(["SYSTEM", "MANUAL"]).nullable(),
  order: orderSchema.nullable(),
  cancel: cancelSchema.nullable(),
  actions: actionsSchema.nullable(),
  lockVersion: z.number().nullable()
});
export type QueryPatientEducationRes = z.infer<typeof queryPatientEducationResSchema>;

export const queryEncounterByMrnResSchema = z.object({
  encounterId: z.string().nullable(),
  encounterNo: z.string().nullable(),
  mrn: z.string().nullable(),
  patientName: z.string().nullable(),
  bedNo: z.string().nullable(),
  attendingDoctor: z.string().nullable(),
  admissionDatetime: z.string().nullable(),
  dischargeDatetime: z.string().nullable(),
  identityType: z.string().nullable()
});
export type QueryEncounterByMrnRes = z.infer<typeof queryEncounterByMrnResSchema>;

export const queryEncounterCardResSchema = z.object({
  id: z.string().nullable(),
  encounterType: z.string().nullable(),
  visitStartDatetime: z.string().nullable(),
  visitEndDatetime: z.string().nullable(),
  doctorName: z.string().nullable(),
  divName: z.string().nullable(),
  icdCode: z.string().nullable(),
  diagnosisDesc: z.string().nullable(),
  allergenNames: z.array(z.string()).nullable(),
  roomNo: z.string().nullable(),
  bedNo: z.string().nullable(),
  seqNo: z.string().nullable(),
  bodyWeight: z.number().nullable(),
  effectiveDatetime: z.string().nullable(),
  surgeryDatetimeList: z.array(z.string()).nullable()
});
export type QueryEncounterCardRes = z.infer<typeof queryEncounterCardResSchema>;

export const queryEncounterResSchema = z.object({
  encounterId: z.string().nullable(),
  encounterNo: z.string().nullable(),
  mrn: z.string().nullable(),
  patientName: z.string().nullable(),
  bedNo: z.string().nullable(),
  attendingDoctor: z.string().nullable(),
  admissionDatetime: z.string().nullable(),
  dischargeDatetime: z.string().nullable(),
  identityType: z.string().nullable()
});
export type QueryEncounterRes = z.infer<typeof queryEncounterResSchema>;

export const queryEventResSchema = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
  parentId: z.string().nullable(),
  forms: z.array(queryFormByEventIdResSchema).nullable(),
  children: z.unknown().nullable()
});
export type QueryEventRes = z.infer<typeof queryEventResSchema>;

export const queryPatientCardResSchema = z.object({
  patientId: z.string().nullable(),
  mrn: z.string().nullable(),
  patientName: z.string().nullable(),
  gender: z.string().nullable(),
  birthDate: z.string().nullable(),
  catastrophicIllnessFlag: z.boolean().nullable(),
  dnrFlag: z.boolean().nullable(),
  alertFlag: z.boolean().nullable(),
  allergyFlag: z.boolean().nullable(),
  isolationFlag: z.boolean().nullable(),
  fastingFlag: z.boolean().nullable(),
  icCardRemarkList: z.array(z.string()).nullable(),
  tenantId: z.string().nullable(),
  genderI18n: z.string().nullable()
});
export type QueryPatientCardRes = z.infer<typeof queryPatientCardResSchema>;

export const queryQrcodeLinkIdResSchema = z.object({
  qrCodeLinkId: z.string().nullable()
});
export type QueryQrcodeLinkIdRes = z.infer<typeof queryQrcodeLinkIdResSchema>;

export const queryPreviousRuntimeFormResSchema = z.object({
  id: z.string().nullable(),
  templateId: z.string().nullable(),
  mrn: z.string().nullable(),
  runtimeContent: z.record(z.string(), z.unknown()).nullable(),
  withPreviousDataFlag: z.boolean().nullable()
});
export type QueryPreviousRuntimeFormRes = z.infer<typeof queryPreviousRuntimeFormResSchema>;

export const queryRuntimeEncounterDataResSchema = z.object({
  encounterId: z.string().nullable(),
  attendingDoctor: z.string().nullable(),
  admissionDateTime: z.string().nullable(),
  admissionIcdCmCode: z.string().nullable(),
  admissionIcdCmName: z.string().nullable(),
  patientSource: z.string().nullable(),
  bedNo: z.string().nullable()
});
export type QueryRuntimeEncounterDataRes = z.infer<typeof queryRuntimeEncounterDataResSchema>;

export const queryRuntimeFormByIdResSchema = z.object({
  formCode: z.string().nullable(),
  templateId: z.string().nullable(),
  name: z.string().nullable(),
  category: formCodeDtoSchema.nullable(),
  type: formCodeDtoSchema.nullable(),
  specialty: formCodeDtoSchema.nullable(),
  changeVersion: z.number().nullable(),
  schemaVersion: z.string().nullable(),
  runtimeContent: z.record(z.string(), z.unknown()).nullable(),
  createDatetime: z.string().nullable(),
  createUser: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  modifyUser: z.string().nullable(),
  approveDatetime: z.string().nullable(),
  approveUser: z.string().nullable(),
  signDatetime: z.string().nullable(),
  signUser: z.string().nullable(),
  lockVersion: z.number().nullable(),
  status: z.enum(["draft", "completed", "pendingReview", "pendingSignature", "pendingReviewAndSignature", "signed", "cancelled"]).nullable(),
  template: formTemplateDtoSchema.nullable()
});
export type QueryRuntimeFormByIdRes = z.infer<typeof queryRuntimeFormByIdResSchema>;

export const queryRuntimeFormTodoByIdResSchema = z.object({
  formCode: z.string().nullable(),
  templateId: z.string().nullable(),
  name: z.string().nullable(),
  category: formCodeDtoSchema.nullable(),
  type: formCodeDtoSchema.nullable(),
  specialty: formCodeDtoSchema.nullable(),
  changeVersion: z.number().nullable(),
  schemaVersion: z.string().nullable(),
  runtimeContent: z.record(z.string(), z.unknown()).nullable(),
  createDatetime: z.string().nullable(),
  createUser: z.string().nullable(),
  modifyUser: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  approveUser: z.string().nullable(),
  approveDatetime: z.string().nullable(),
  signDatetime: z.string().nullable(),
  signUser: z.string().nullable(),
  lockVersion: z.number().nullable(),
  status: z.enum(["pending", "draft", "completed", "cancelled"]).nullable(),
  template: formTemplateDtoSchema.nullable()
});
export type QueryRuntimeFormTodoByIdRes = z.infer<typeof queryRuntimeFormTodoByIdResSchema>;

export const queryRuntimeFormTodoResSchema = z.object({
  id: z.string().nullable(),
  templateId: z.string().nullable(),
  formCode: z.string().nullable(),
  name: z.string().nullable(),
  category: formCodeDtoSchema.nullable(),
  type: formCodeDtoSchema.nullable(),
  specialty: formCodeDtoSchema.nullable(),
  status: z.enum(["pending", "draft", "completed", "cancelled"]).nullable(),
  createUserName: z.string().nullable(),
  createDatetime: z.string().nullable(),
  modifyUserName: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  effectiveDatetime: z.string().nullable()
});
export type QueryRuntimeFormTodoRes = z.infer<typeof queryRuntimeFormTodoResSchema>;

export const queryRuntimeFormResSchema = z.object({
  id: z.string().nullable(),
  rowType: z.enum(["TODO", "FORM"]).nullable(),
  templateId: z.string().nullable(),
  formCode: z.string().nullable(),
  name: z.string().nullable(),
  category: formCodeDtoSchema.nullable(),
  type: formCodeDtoSchema.nullable(),
  specialty: formCodeDtoSchema.nullable(),
  createUserName: z.string().nullable(),
  createDatetime: z.string().nullable(),
  modifyUserName: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  status: z.enum(["draft", "completed", "pendingReview", "pendingSignature", "pendingReviewAndSignature", "signed", "cancelled"]).nullable(),
  allowCreateFlag: z.boolean().nullable(),
  todoStatus: z.enum(["pending", "draft", "completed", "cancelled"]).nullable(),
  effectiveDatetime: z.string().nullable()
});
export type QueryRuntimeFormRes = z.infer<typeof queryRuntimeFormResSchema>;

export const queryRuntimePatientDataResSchema = z.object({
  patientName: z.string().nullable(),
  mrn: z.string().nullable(),
  idNo: z.string().nullable(),
  birthDatetime: z.string().nullable(),
  gender: z.string().nullable()
});
export type QueryRuntimePatientDataRes = z.infer<typeof queryRuntimePatientDataResSchema>;

export const queryFormCodeByTypeResSchema = z.object({
  id: z.string().nullable(),
  code: z.string().nullable(),
  displayName: z.string().nullable()
});
export type QueryFormCodeByTypeRes = z.infer<typeof queryFormCodeByTypeResSchema>;

export const formParameterResSchema = z.object({
  id: z.string().nullable(),
  paramCode: z.string().nullable(),
  aliasName: z.string().nullable(),
  groupName: z.string().nullable(),
  multipleAllowedFlag: z.boolean().nullable(),
  parentId: z.string().nullable(),
  children: z.unknown().nullable()
});
export type FormParameterRes = z.infer<typeof formParameterResSchema>;

export const queryTemplateByIdResSchema = z.object({
  id: z.string().nullable(),
  templateCode: z.string().nullable(),
  name: z.string().nullable(),
  category: formCodeDtoSchema.nullable(),
  type: formCodeDtoSchema.nullable(),
  specialty: formCodeDtoSchema.nullable(),
  lockVersion: z.number().nullable(),
  changeVersion: z.number().nullable(),
  schemaVersion: z.string().nullable(),
  templateContent: z.record(z.string(), z.unknown()).nullable(),
  modifyUserName: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  status: z.string().nullable(),
  isOutdated: z.boolean().nullable()
});
export type QueryTemplateByIdRes = z.infer<typeof queryTemplateByIdResSchema>;

export const queryTemplateConfigResSchema = z.object({
  id: z.string().nullable(),
  changeVersion: z.number().nullable(),
  formLimitCount: z.number().nullable(),
  withPreviousDataFlag: z.boolean().nullable(),
  withSignatureFlag: z.boolean().nullable(),
  withDoctorApproveFlag: z.boolean().nullable(),
  esignFlag: z.boolean().nullable(),
  qrCodeFlag: z.boolean().nullable(),
  disabledDatetime: z.string().nullable(),
  workflows: z.array(workFlowDtoSchema).nullable(),
  lockVersion: z.number().nullable()
});
export type QueryTemplateConfigRes = z.infer<typeof queryTemplateConfigResSchema>;

export const queryTemplateResSchema = z.object({
  id: z.string().nullable(),
  templateCode: z.string().nullable(),
  name: z.string().nullable(),
  category: formCodeDtoSchema.nullable(),
  type: formCodeDtoSchema.nullable(),
  specialty: formCodeDtoSchema.nullable(),
  changeVersion: z.number().nullable(),
  modifyUserName: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  status: z.string().nullable(),
  disabled: z.boolean().nullable()
});
export type QueryTemplateRes = z.infer<typeof queryTemplateResSchema>;

export const queryVersionControlResSchema = z.object({
  id: z.string().nullable(),
  templateCode: z.string().nullable(),
  name: z.string().nullable(),
  category: formCodeDtoSchema.nullable(),
  type: formCodeDtoSchema.nullable(),
  specialty: formCodeDtoSchema.nullable(),
  lockVersion: z.number().nullable(),
  changeVersion: z.number().nullable(),
  schemaVersion: z.string().nullable(),
  templateContent: z.record(z.string(), z.unknown()).nullable(),
  modifyUserName: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  status: z.string().nullable()
});
export type QueryVersionControlRes = z.infer<typeof queryVersionControlResSchema>;

export const querySystemConfigTemplateResSchema = z.object({
  id: z.string().nullable(),
  templateCode: z.string().nullable(),
  name: z.string().nullable(),
  category: formCodeDtoSchema.nullable(),
  type: formCodeDtoSchema.nullable(),
  specialty: formCodeDtoSchema.nullable(),
  lockVersion: z.number().nullable(),
  changeVersion: z.number().nullable(),
  schemaVersion: z.string().nullable(),
  templateContent: z.record(z.string(), z.unknown()).nullable(),
  modifyUserName: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  status: z.string().nullable(),
  isOutdated: z.boolean().nullable(),
  extraParamCode: z.record(z.string(), z.array(z.string())).nullable()
});
export type QuerySystemConfigTemplateRes = z.infer<typeof querySystemConfigTemplateResSchema>;

export const lockStatusResSchema = z.object({
  locked: z.boolean().nullable(),
  payload: leaseLockPayloadDtoSchema.nullable(),
  pttlMs: z.number().nullable()
});
export type LockStatusRes = z.infer<typeof lockStatusResSchema>;

export const optionResSchema = z.object({
  id: z.string().nullable(),
  code: z.string().nullable(),
  displayName: z.string().nullable()
});
export type OptionRes = z.infer<typeof optionResSchema>;

export const updateRuntimeFormStatusResSchema = z.object({
  id: z.string().nullable(),
  formCode: z.string().nullable(),
  templateId: z.string().nullable(),
  name: z.string().nullable(),
  schemaVersion: z.string().nullable(),
  mrn: z.string().nullable(),
  status: z.enum(["draft", "completed", "pendingReview", "pendingSignature", "pendingReviewAndSignature", "signed", "cancelled"]).nullable(),
  createDatetime: z.string().nullable(),
  createUser: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  modifyUser: z.string().nullable(),
  approveDatetime: z.string().nullable(),
  approveUser: z.string().nullable(),
  signDatetime: z.string().nullable(),
  signUser: z.string().nullable(),
  lockVersion: z.number().nullable()
});
export type UpdateRuntimeFormStatusRes = z.infer<typeof updateRuntimeFormStatusResSchema>;

export const savedComponentCategoryResSchema = z.object({
  id: z.string().nullable(),
  categoryName: z.string().nullable(),
  parentId: z.string().nullable(),
  sort: z.number(),
  lockVersion: z.number().nullable()
});
export type SavedComponentCategoryRes = z.infer<typeof savedComponentCategoryResSchema>;

export const saveFormToEventResSchema = z.object({
  recordStatus: z.enum(["C", "U", "D", "R"]).nullable(),
  id: z.string().nullable(),
  createDatetime: z.string().nullable(),
  createUser: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  modifyUser: z.string().nullable(),
  lockVersion: z.number().nullable(),
  eventId: z.string().nullable(),
  templateId: z.string().nullable()
});
export type SaveFormToEventRes = z.infer<typeof saveFormToEventResSchema>;

export const queryFormCancelStatusResSchema = z.object({
  cancelFlag: z.boolean().nullable()
});
export type QueryFormCancelStatusRes = z.infer<typeof queryFormCancelStatusResSchema>;

export const queryRuntimeDataBatchResSchema = z.object({
  results: z.record(z.string(), z.array(z.string())).nullable()
});
export type QueryRuntimeDataBatchRes = z.infer<typeof queryRuntimeDataBatchResSchema>;

export const runtimeDataDtoSchema = z.object({
  paramCode: z.enum(["@mrn", "@patientName", "@birthDate", "@age", "@gender", "@identityDocumentType", "@id", "@mobilePhone", "@homePhone", "@mailingAddress", "@educationLevel", "@patientCategory", "@relationship1", "@name1", "@contactMobilePhone1", "@contactHomePhone1", "@relationship2", "@name2", "@contactMobilePhone2", "@contactHomePhone2", "@allergyHistory", "@encounterNumber", "@encounterCategory", "@encounterDateTime", "@treatmentEndDateTime", "@admissionDateTime", "@dischargeDateTime", "@transferDateTime", "@attendingPhysicianCode", "@attendingPhysicianName", "@departmentCode", "@departmentName", "@bedNumber", "@clinicRoomNumber", "@clinicRoomName", "@copaymentCode", "@encounterSequenceNumber", "@chiefComplaint", "@primaryDiagnosisCode", "@primaryDiagnosisName", "@secondaryDiagnosisCode", "@secondaryDiagnosisName", "@primaryProcedureCode", "@primaryProcedureName", "@secondaryProcedureCode", "@secondaryProcedureName", "@transferInBedNumber", "@wardFeeDiffSelfPay", "@orderId", "@orderDateTime", "@orderCategory", "@orderCode", "@orderName", "@dosage", "@dosageUnit", "@frequency", "@route", "@administrationMethod", "@days", "@totalQuantity", "@totalQuantityUnit", "@billingCategory", "@requisitionNumber", "@unitPrice", "@paymentPercentage", "@totalPrice", "@executionStartDateTime", "@executionEndDateTime", "@performer", "@doctor", "@nurse", "@familyMember", "@occupation", "@marriage", "@religion", "@admissionMethod", "@currentUser", "@institutionCode", "@institutionName", "@institutionAddress", "@institutionPhone", "@responsiblePerson", "@doctorSpecialty", "@doctorLicenseNumber"]),
  keyword: z.string().nullable(),
  limit: z.number().nullable()
});
export type RuntimeDataDto = z.infer<typeof runtimeDataDtoSchema>;

export const saveRuntimeFormResSchema = z.object({
  id: z.string().nullable(),
  formCode: z.string().nullable(),
  templateId: z.string().nullable(),
  name: z.string().nullable(),
  changeVersion: z.number().nullable(),
  schemaVersion: z.string().nullable(),
  mrn: z.string().nullable(),
  status: z.enum(["draft", "completed", "pendingReview", "pendingSignature", "pendingReviewAndSignature", "signed", "cancelled"]).nullable(),
  lockVersion: z.number().nullable(),
  versionKey: z.string().nullable()
});
export type SaveRuntimeFormRes = z.infer<typeof saveRuntimeFormResSchema>;

export const formCodeResSchema = z.object({
  id: z.string().nullable(),
  codeType: z.string().nullable(),
  code: z.string().nullable(),
  displayName: z.string().nullable(),
  parentId: z.string().nullable(),
  children: z.array(z.unknown()).nullable()
});
export type FormCodeRes = z.infer<typeof formCodeResSchema>;

export const conditionDtoSchema = z.object({
  type: z.string().nullable()
});
export type ConditionDto = z.infer<typeof conditionDtoSchema>;

export const saveTemplateResSchema = z.object({
  id: z.string().nullable(),
  templateCode: z.string().min(1),
  name: z.string().min(1),
  changeVersion: z.number().nullable(),
  schemaVersion: z.string().nullable(),
  status: z.string().nullable(),
  lockVersion: z.number().nullable()
});
export type SaveTemplateRes = z.infer<typeof saveTemplateResSchema>;

export const acquireLockResSchema = z.object({
  ok: z.boolean().nullable(),
  payload: leaseLockPayloadDtoSchema.nullable(),
  pttlMs: z.number().nullable(),
  msg: z.string().nullable()
});
export type AcquireLockRes = z.infer<typeof acquireLockResSchema>;

export const releaseLockResSchema = z.object({
  ok: z.boolean().nullable(),
  released: z.boolean().nullable(),
  msg: z.string().nullable()
});
export type ReleaseLockRes = z.infer<typeof releaseLockResSchema>;

export const renewLockResSchema = z.object({
  ok: z.boolean().nullable(),
  pttlMs: z.number().nullable(),
  msg: z.string().nullable()
});
export type RenewLockRes = z.infer<typeof renewLockResSchema>;

export const updateComponentExcludeContentResSchema = z.object({
  id: z.string().nullable(),
  formComponentName: z.string().nullable(),
  formComponentCategoryId: z.string().nullable(),
  sort: z.number().nullable(),
  lockVersion: z.number().nullable()
});
export type UpdateComponentExcludeContentRes = z.infer<typeof updateComponentExcludeContentResSchema>;

export const savedComponentResSchema = z.object({
  id: z.string().nullable(),
  formComponentName: z.string().nullable(),
  formComponentCategoryId: z.string().nullable(),
  componentContent: z.string().nullable(),
  sort: z.number().nullable(),
  lockVersion: z.number().nullable()
});
export type SavedComponentRes = z.infer<typeof savedComponentResSchema>;

export const updateFormCancelResSchema = z.object({
  formId: z.string().nullable(),
  cancelFlag: z.boolean().nullable(),
  cancelReason: z.string().nullable(),
  cancelDatetime: z.string().nullable(),
  lockVersion: z.number().nullable()
});
export type UpdateFormCancelRes = z.infer<typeof updateFormCancelResSchema>;

export const updateRuntimeFormTodoResSchema = z.object({
  id: z.string().nullable(),
  formCode: z.string().nullable(),
  templateId: z.string().nullable(),
  name: z.string().nullable(),
  schemaVersion: z.string().nullable(),
  mrn: z.string().nullable(),
  status: z.string().nullable(),
  lockVersion: z.number().nullable()
});
export type UpdateRuntimeFormTodoRes = z.infer<typeof updateRuntimeFormTodoResSchema>;

export const updateTemplateResSchema = z.object({
  id: z.string().nullable(),
  frontendKey: z.string().nullable(),
  templateCode: z.string().nullable(),
  name: z.string().nullable(),
  categoryId: z.string().nullable(),
  typeId: z.string().nullable(),
  specialtyId: z.string().nullable(),
  schemaVersion: z.string().nullable(),
  status: z.enum(["DRAFT", "PUBLISHED", "PUBLISHED_DRAFT"]).nullable(),
  modifyUser: z.string().nullable(),
  modifyDatetime: z.string().nullable()
});
export type UpdateTemplateRes = z.infer<typeof updateTemplateResSchema>;

export const apiResponseListComponentCategoryResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(componentCategoryResSchema).nullable()
});
export type ApiResponseListComponentCategoryRes = z.infer<typeof apiResponseListComponentCategoryResSchema>;

export const apiResponseComponentResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: componentResSchema.nullable()
});
export type ApiResponseComponentRes = z.infer<typeof apiResponseComponentResSchema>;

export const apiPageResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable(),
  totalPageSize: z.number().nullable(),
  totalItemSize: z.number().nullable(),
  currentPageItemSize: z.number().nullable(),
  pageSize: z.number().nullable()
});
export type ApiPageResponseObject = z.infer<typeof apiPageResponseObjectSchema>;

export const apiResponseListQueryPatientEducationResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryPatientEducationResSchema).nullable()
});
export type ApiResponseListQueryPatientEducationRes = z.infer<typeof apiResponseListQueryPatientEducationResSchema>;

export const apiResponseListQueryEncounterByMrnResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryEncounterByMrnResSchema).nullable()
});
export type ApiResponseListQueryEncounterByMrnRes = z.infer<typeof apiResponseListQueryEncounterByMrnResSchema>;

export const apiResponseQueryEncounterCardResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryEncounterCardResSchema.nullable()
});
export type ApiResponseQueryEncounterCardRes = z.infer<typeof apiResponseQueryEncounterCardResSchema>;

export const apiResponseListQueryEncounterResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryEncounterResSchema).nullable()
});
export type ApiResponseListQueryEncounterRes = z.infer<typeof apiResponseListQueryEncounterResSchema>;

export const apiResponseBooleanSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.boolean().nullable()
});
export type ApiResponseBoolean = z.infer<typeof apiResponseBooleanSchema>;

export const apiResponseListQueryEventResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryEventResSchema).nullable()
});
export type ApiResponseListQueryEventRes = z.infer<typeof apiResponseListQueryEventResSchema>;

export const apiResponseListQueryFormByEventIdResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryFormByEventIdResSchema).nullable()
});
export type ApiResponseListQueryFormByEventIdRes = z.infer<typeof apiResponseListQueryFormByEventIdResSchema>;

export const apiResponseQueryPatientCardResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryPatientCardResSchema.nullable()
});
export type ApiResponseQueryPatientCardRes = z.infer<typeof apiResponseQueryPatientCardResSchema>;

export const apiResponseQueryQrcodeLinkIdResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryQrcodeLinkIdResSchema.nullable()
});
export type ApiResponseQueryQrcodeLinkIdRes = z.infer<typeof apiResponseQueryQrcodeLinkIdResSchema>;

export const apiResponseMapStringObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.record(z.string(), z.unknown()).nullable()
});
export type ApiResponseMapStringObject = z.infer<typeof apiResponseMapStringObjectSchema>;

export const apiResponseQueryPreviousRuntimeFormResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryPreviousRuntimeFormResSchema.nullable()
});
export type ApiResponseQueryPreviousRuntimeFormRes = z.infer<typeof apiResponseQueryPreviousRuntimeFormResSchema>;

export const apiResponseListStringSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(z.string()).nullable()
});
export type ApiResponseListString = z.infer<typeof apiResponseListStringSchema>;

export const apiResponseQueryRuntimeEncounterDataResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryRuntimeEncounterDataResSchema.nullable()
});
export type ApiResponseQueryRuntimeEncounterDataRes = z.infer<typeof apiResponseQueryRuntimeEncounterDataResSchema>;

export const apiResponseQueryRuntimeFormByIdResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryRuntimeFormByIdResSchema.nullable()
});
export type ApiResponseQueryRuntimeFormByIdRes = z.infer<typeof apiResponseQueryRuntimeFormByIdResSchema>;

export const apiResponseQueryRuntimeFormTodoByIdResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryRuntimeFormTodoByIdResSchema.nullable()
});
export type ApiResponseQueryRuntimeFormTodoByIdRes = z.infer<typeof apiResponseQueryRuntimeFormTodoByIdResSchema>;

export const apiResponseListQueryRuntimeFormTodoResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryRuntimeFormTodoResSchema).nullable()
});
export type ApiResponseListQueryRuntimeFormTodoRes = z.infer<typeof apiResponseListQueryRuntimeFormTodoResSchema>;

export const apiResponseListQueryRuntimeFormResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryRuntimeFormResSchema).nullable()
});
export type ApiResponseListQueryRuntimeFormRes = z.infer<typeof apiResponseListQueryRuntimeFormResSchema>;

export const apiResponseQueryRuntimePatientDataResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryRuntimePatientDataResSchema.nullable()
});
export type ApiResponseQueryRuntimePatientDataRes = z.infer<typeof apiResponseQueryRuntimePatientDataResSchema>;

export const apiResponseListQueryFormCodeByTypeResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryFormCodeByTypeResSchema).nullable()
});
export type ApiResponseListQueryFormCodeByTypeRes = z.infer<typeof apiResponseListQueryFormCodeByTypeResSchema>;

export const apiResponseListFormCodeResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(z.object({
  id: z.string().nullable(),
  codeType: z.string().nullable(),
  code: z.string().nullable(),
  displayName: z.string().nullable(),
  parentId: z.string().nullable(),
  children: z.array(z.unknown()).nullable()
}))
});
export type ApiResponseListFormCodeRes = z.infer<typeof apiResponseListFormCodeResSchema>;

export const apiResponseListFormParameterResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(formParameterResSchema).nullable()
});
export type ApiResponseListFormParameterRes = z.infer<typeof apiResponseListFormParameterResSchema>;

export const apiResponseQueryTemplateByIdResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryTemplateByIdResSchema.nullable()
});
export type ApiResponseQueryTemplateByIdRes = z.infer<typeof apiResponseQueryTemplateByIdResSchema>;

export const apiResponseQueryTemplateConfigResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryTemplateConfigResSchema.nullable()
});
export type ApiResponseQueryTemplateConfigRes = z.infer<typeof apiResponseQueryTemplateConfigResSchema>;

export const apiResponseListQueryTemplateResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryTemplateResSchema).nullable()
});
export type ApiResponseListQueryTemplateRes = z.infer<typeof apiResponseListQueryTemplateResSchema>;

export const apiResponseListQueryVersionControlResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryVersionControlResSchema).nullable()
});
export type ApiResponseListQueryVersionControlRes = z.infer<typeof apiResponseListQueryVersionControlResSchema>;

export const apiResponseQuerySystemConfigTemplateResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: querySystemConfigTemplateResSchema.nullable()
});
export type ApiResponseQuerySystemConfigTemplateRes = z.infer<typeof apiResponseQuerySystemConfigTemplateResSchema>;

export const apiResponseLockStatusResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: lockStatusResSchema.nullable()
});
export type ApiResponseLockStatusRes = z.infer<typeof apiResponseLockStatusResSchema>;

export const apiResponseMapStringListOptionResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.record(z.string(), z.array(optionResSchema)).nullable()
});
export type ApiResponseMapStringListOptionRes = z.infer<typeof apiResponseMapStringListOptionResSchema>;

export const apiResponseListOptionResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(optionResSchema).nullable()
});
export type ApiResponseListOptionRes = z.infer<typeof apiResponseListOptionResSchema>;

export const apiResponseUpdateRuntimeFormStatusResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: updateRuntimeFormStatusResSchema.nullable()
});
export type ApiResponseUpdateRuntimeFormStatusRes = z.infer<typeof apiResponseUpdateRuntimeFormStatusResSchema>;

export const updateRuntimeFormStatusReqSchema = z.object({
  id: z.string().min(1),
  targetStatus: z.number(),
  lockVersion: z.number()
});
export type UpdateRuntimeFormStatusReq = z.infer<typeof updateRuntimeFormStatusReqSchema>;

export const apiResponseSavedComponentCategoryResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: savedComponentCategoryResSchema.nullable()
});
export type ApiResponseSavedComponentCategoryRes = z.infer<typeof apiResponseSavedComponentCategoryResSchema>;

export const addCategoryReqSchema = z.object({
  categoryName: z.string().min(1),
  parentId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  sort: z.preprocess((val) => val === undefined ? null : val, z.number().nullish())
});
export type AddCategoryReq = z.infer<typeof addCategoryReqSchema>;

export const addComponentReqSchema = z.object({
  formComponentName: z.string().min(1),
  formComponentCategoryId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  componentContent: z.string().min(1),
  sort: z.preprocess((val) => val === undefined ? null : val, z.number().nullish())
});
export type AddComponentReq = z.infer<typeof addComponentReqSchema>;

export const apiResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable()
});
export type ApiResponseObject = z.infer<typeof apiResponseObjectSchema>;

export const reqCrudSaveSchema = z.object({
  systemNo: z.string().min(1),
  tableName: z.string().min(1),
  tableData: z.preprocess((val) => val === undefined ? null : val, z.array(z.unknown()).nullish())
});
export type ReqCrudSave = z.infer<typeof reqCrudSaveSchema>;

export const addEventReqSchema = z.object({
  name: z.string().min(1),
  parentId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish())
});
export type AddEventReq = z.infer<typeof addEventReqSchema>;

export const apiResponseListSaveFormToEventResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(saveFormToEventResSchema).nullable()
});
export type ApiResponseListSaveFormToEventRes = z.infer<typeof apiResponseListSaveFormToEventResSchema>;

export const saveFormToEventReqSchema = z.object({
  id: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  eventId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  templateId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  lockVersion: z.preprocess((val) => val === undefined ? null : val, z.number().nullish()),
  recordStatus: z.preprocess((val) => val === undefined ? null : val, z.enum(["C", "U", "D", "R"]).nullish())
});
export type SaveFormToEventReq = z.infer<typeof saveFormToEventReqSchema>;

export const apiResponseQueryFormCancelStatusResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryFormCancelStatusResSchema.nullable()
});
export type ApiResponseQueryFormCancelStatusRes = z.infer<typeof apiResponseQueryFormCancelStatusResSchema>;

export const queryFormCancelStatusReqSchema = z.object({
  formId: z.string().min(1),
  rowType: z.enum(["TODO", "FORM"])
});
export type QueryFormCancelStatusReq = z.infer<typeof queryFormCancelStatusReqSchema>;

export const apiResponseQueryRuntimeDataBatchResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryRuntimeDataBatchResSchema.nullable()
});
export type ApiResponseQueryRuntimeDataBatchRes = z.infer<typeof apiResponseQueryRuntimeDataBatchResSchema>;

export const queryRuntimeDataBatchReqSchema = z.object({
  mrn: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  encounterId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  parameters: z.array(runtimeDataDtoSchema)
});
export type QueryRuntimeDataBatchReq = z.infer<typeof queryRuntimeDataBatchReqSchema>;

export const apiResponseSaveRuntimeFormResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: saveRuntimeFormResSchema.nullable()
});
export type ApiResponseSaveRuntimeFormRes = z.infer<typeof apiResponseSaveRuntimeFormResSchema>;

export const saveRuntimeFormReqSchema = z.object({
  id: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  templateId: z.string().min(1),
  runtimeContent: z.preprocess((val) => val === undefined ? null : val, z.record(z.string(), z.unknown()).nullish()),
  mrn: z.string().min(1),
  encounterId: z.string().min(1),
  isDraft: z.boolean(),
  lockVersion: z.number(),
  signedCount: z.number(),
  formHtml: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish())
});
export type SaveRuntimeFormReq = z.infer<typeof saveRuntimeFormReqSchema>;

export const apiResponseFormCodeResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: formCodeResSchema.nullable()
});
export type ApiResponseFormCodeRes = z.infer<typeof apiResponseFormCodeResSchema>;

export const addFormCodeReqSchema = z.object({
  codeType: z.string().min(1),
  code: z.string().min(1),
  displayName: z.string().min(1),
  parentId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish())
});
export type AddFormCodeReq = z.infer<typeof addFormCodeReqSchema>;

export const addFormParameterReqSchema = z.object({
  paramCode: z.string().min(1),
  groupName: z.string().min(1),
  aliasName: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  multipleAllowedFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish()),
  parentId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish())
});
export type AddFormParameterReq = z.infer<typeof addFormParameterReqSchema>;

export const addTemplateReqSchema = z.object({
  templateCode: z.string().min(1),
  name: z.string().min(1),
  categoryId: z.string().min(1),
  typeId: z.string().min(1),
  specialtyId: z.string().min(1),
  schemaVersion: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  templateContent: z.preprocess((val) => val === undefined ? null : val, z.record(z.string(), z.unknown()).nullish()),
  isDraft: z.boolean(),
  signatureCount: z.number()
});
export type AddTemplateReq = z.infer<typeof addTemplateReqSchema>;

export const apiResponseStringSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.string().nullable()
});
export type ApiResponseString = z.infer<typeof apiResponseStringSchema>;

export const singleConditionDtoSchema = z.object({
  type: z.enum(["single", "group"]).nullable(),
  field: z.string().nullable(),
  operator: z.string().nullable(),
  value: z.string().nullable(),
  conjunction: z.string().nullable()
});
export type SingleConditionDto = z.infer<typeof singleConditionDtoSchema>;

export const saveTemplateConfigReqSchema = z.object({
  id: z.string().min(1),
  formLimitCount: z.preprocess((val) => val === undefined ? null : val, z.number().nullish()),
  withPreviousDataFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish()),
  withSignatureFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish()),
  withDoctorApproveFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish()),
  esignFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish()),
  qrCodeFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish()),
  disabledDatetime: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  workflows: z.preprocess((val) => val === undefined ? null : val, z.array(workFlowDtoSchema).nullish()),
  lockVersion: z.preprocess((val) => val === undefined ? null : val, z.number().nullish())
});
export type SaveTemplateConfigReq = z.infer<typeof saveTemplateConfigReqSchema>;

export const groupConditionDtoSchema = z.object({
  type: z.enum(["single", "group"]).nullable(),
  conditions: z.unknown().nullable()
});
export type GroupConditionDto = z.infer<typeof groupConditionDtoSchema>;

export const apiResponseSaveTemplateResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: saveTemplateResSchema.nullable()
});
export type ApiResponseSaveTemplateRes = z.infer<typeof apiResponseSaveTemplateResSchema>;

export const saveTemplateReqSchema = z.object({
  id: z.string().min(1),
  templateCode: z.string().min(1),
  name: z.string().min(1),
  categoryId: z.string().min(1),
  typeId: z.string().min(1),
  specialtyId: z.string().min(1),
  schemaVersion: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  templateContent: z.preprocess((val) => val === undefined ? null : val, z.record(z.string(), z.unknown()).nullish()),
  isDraft: z.boolean(),
  lockVersion: z.number(),
  signatureCount: z.number()
});
export type SaveTemplateReq = z.infer<typeof saveTemplateReqSchema>;

export const apiResponseAcquireLockResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: acquireLockResSchema.nullable()
});
export type ApiResponseAcquireLockRes = z.infer<typeof apiResponseAcquireLockResSchema>;

export const apiResponseReleaseLockResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: releaseLockResSchema.nullable()
});
export type ApiResponseReleaseLockRes = z.infer<typeof apiResponseReleaseLockResSchema>;

export const apiResponseRenewLockResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: renewLockResSchema.nullable()
});
export type ApiResponseRenewLockRes = z.infer<typeof apiResponseRenewLockResSchema>;

export const apiResponseListSavedComponentCategoryResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(savedComponentCategoryResSchema).nullable()
});
export type ApiResponseListSavedComponentCategoryRes = z.infer<typeof apiResponseListSavedComponentCategoryResSchema>;

export const updateCategoryReqSchema = z.object({
  id: z.string().min(1),
  categoryName: z.string().min(1),
  parentId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  sort: z.number(),
  lockVersion: z.number()
});
export type UpdateCategoryReq = z.infer<typeof updateCategoryReqSchema>;

export const apiResponseListUpdateComponentExcludeContentResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(updateComponentExcludeContentResSchema).nullable()
});
export type ApiResponseListUpdateComponentExcludeContentRes = z.infer<typeof apiResponseListUpdateComponentExcludeContentResSchema>;

export const updateComponentExcludeContentReqSchema = z.object({
  id: z.string().min(1),
  formComponentName: z.string().min(1),
  formComponentCategoryId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  sort: z.number(),
  lockVersion: z.number()
});
export type UpdateComponentExcludeContentReq = z.infer<typeof updateComponentExcludeContentReqSchema>;

export const apiResponseSavedComponentResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: savedComponentResSchema.nullable()
});
export type ApiResponseSavedComponentRes = z.infer<typeof apiResponseSavedComponentResSchema>;

export const updateComponentReqSchema = z.object({
  id: z.string().min(1),
  formComponentName: z.string().min(1),
  formComponentCategoryId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  componentContent: z.string().min(1),
  sort: z.number(),
  lockVersion: z.number()
});
export type UpdateComponentReq = z.infer<typeof updateComponentReqSchema>;

export const apiResponseUpdateFormCancelResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: updateFormCancelResSchema.nullable()
});
export type ApiResponseUpdateFormCancelRes = z.infer<typeof apiResponseUpdateFormCancelResSchema>;

export const updateFormCancelReqSchema = z.object({
  formId: z.string().min(1),
  rowType: z.enum(["TODO", "FORM"]),
  cancelReason: z.string().min(1),
  lockVersion: z.number()
});
export type UpdateFormCancelReq = z.infer<typeof updateFormCancelReqSchema>;

export const apiResponseUpdateRuntimeFormTodoResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: updateRuntimeFormTodoResSchema.nullable()
});
export type ApiResponseUpdateRuntimeFormTodoRes = z.infer<typeof apiResponseUpdateRuntimeFormTodoResSchema>;

export const updateRuntimeFormTodoReqSchema = z.object({
  id: z.string().min(1),
  runtimeContent: z.preprocess((val) => val === undefined ? null : val, z.record(z.string(), z.unknown()).nullish()),
  isDraft: z.boolean(),
  lockVersion: z.number(),
  signedCount: z.number(),
  formHtml: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish())
});
export type UpdateRuntimeFormTodoReq = z.infer<typeof updateRuntimeFormTodoReqSchema>;

export const apiResponseUpdateTemplateResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: updateTemplateResSchema.nullable()
});
export type ApiResponseUpdateTemplateRes = z.infer<typeof apiResponseUpdateTemplateResSchema>;

export const updateTemplateReqSchema = z.object({
  id: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  templateCode: z.string().min(1),
  name: z.string().min(1),
  categoryId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  typeId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  specialtyId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  schemaVersion: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  templateContent: z.preprocess((val) => val === undefined ? null : val, z.record(z.string(), z.unknown()).nullish()),
  isDraft: z.boolean(),
  lockVersion: z.number(),
  signatureCount: z.number()
});
export type UpdateTemplateReq = z.infer<typeof updateTemplateReqSchema>;

// Query Parameter Schemas

export const renewLockQuerySchema = z.object({
  token: z.string()
});
export type RenewLockQuery = z.infer<typeof renewLockQuerySchema>;

export const releaseLockQuerySchema = z.object({
  token: z.string()
});
export type ReleaseLockQuery = z.infer<typeof releaseLockQuerySchema>;

export const acquireLockQuerySchema = z.object({
  ownerId: z.string()
});
export type AcquireLockQuery = z.infer<typeof acquireLockQuerySchema>;

export const renewLock_1QuerySchema = z.object({
  token: z.string()
});
export type RenewLock_1Query = z.infer<typeof renewLock_1QuerySchema>;

export const releaseLock_1QuerySchema = z.object({
  token: z.string()
});
export type ReleaseLock_1Query = z.infer<typeof releaseLock_1QuerySchema>;

export const acquireLock_1QuerySchema = z.object({
  ownerId: z.string()
});
export type AcquireLock_1Query = z.infer<typeof acquireLock_1QuerySchema>;

export const queryOrderQuerySchema = z.object({
  keyword: z.string().nullish(),
  limit: z.number().nullish()
});
export type QueryOrderQuery = z.infer<typeof queryOrderQuerySchema>;

export const queryOptionsQuerySchema = z.object({
  optionTypes: z.array(z.enum(["route", "notifyMethod", "freq", "alertIcon", "adminMethod"])),
  keyword: z.string().nullish(),
  limit: z.number().nullish()
});
export type QueryOptionsQuery = z.infer<typeof queryOptionsQuerySchema>;

export const queryNotifiedUserQuerySchema = z.object({
  keyword: z.string().nullish(),
  limit: z.number().nullish()
});
export type QueryNotifiedUserQuery = z.infer<typeof queryNotifiedUserQuerySchema>;

export const queryWardFeeDiffConsentTemplateQuerySchema = z.object({
  transferInBedNumber: z.string().nullish(),
  wardFeeDiffSelfPay: z.string().nullish()
});
export type QueryWardFeeDiffConsentTemplateQuery = z.infer<typeof queryWardFeeDiffConsentTemplateQuerySchema>;

export const queryTemplateQuerySchema = z.object({
  categoryId: z.string().nullish(),
  typeId: z.string().nullish(),
  specialtyId: z.string().nullish()
});
export type QueryTemplateQuery = z.infer<typeof queryTemplateQuerySchema>;

export const queryPublishedTemplateQuerySchema = z.object({
  categoryId: z.string().nullish(),
  typeId: z.string().nullish(),
  specialtyId: z.string().nullish(),
  includeDisabled: z.boolean()
});
export type QueryPublishedTemplateQuery = z.infer<typeof queryPublishedTemplateQuerySchema>;

export const queryDraftTemplateQuerySchema = z.object({
  categoryId: z.string().nullish(),
  typeId: z.string().nullish(),
  specialtyId: z.string().nullish()
});
export type QueryDraftTemplateQuery = z.infer<typeof queryDraftTemplateQuerySchema>;

export const queryRuntimePatientDataQuerySchema = z.object({
  mrn: z.string()
});
export type QueryRuntimePatientDataQuery = z.infer<typeof queryRuntimePatientDataQuerySchema>;

export const queryRuntimeFormQuerySchema = z.object({
  categoryId: z.string().nullish(),
  typeId: z.string().nullish(),
  specialtyId: z.string().nullish(),
  mrn: z.string(),
  encounterId: z.string()
});
export type QueryRuntimeFormQuery = z.infer<typeof queryRuntimeFormQuerySchema>;

export const queryRuntimeFormTodoQuerySchema = z.object({
  categoryId: z.string().nullish(),
  typeId: z.string().nullish(),
  specialtyId: z.string().nullish(),
  mrn: z.string(),
  encounterId: z.string()
});
export type QueryRuntimeFormTodoQuery = z.infer<typeof queryRuntimeFormTodoQuerySchema>;

export const queryRuntimeEncounterDataQuerySchema = z.object({
  encounterId: z.string()
});
export type QueryRuntimeEncounterDataQuery = z.infer<typeof queryRuntimeEncounterDataQuerySchema>;

export const queryRuntimeDataQuerySchema = z.object({
  paramCode: z.array(z.enum(["@mrn", "@patientName", "@birthDate", "@age", "@gender", "@identityDocumentType", "@id", "@mobilePhone", "@homePhone", "@mailingAddress", "@educationLevel", "@patientCategory", "@relationship1", "@name1", "@contactMobilePhone1", "@contactHomePhone1", "@relationship2", "@name2", "@contactMobilePhone2", "@contactHomePhone2", "@allergyHistory", "@encounterNumber", "@encounterCategory", "@encounterDateTime", "@treatmentEndDateTime", "@admissionDateTime", "@dischargeDateTime", "@transferDateTime", "@attendingPhysicianCode", "@attendingPhysicianName", "@departmentCode", "@departmentName", "@bedNumber", "@clinicRoomNumber", "@clinicRoomName", "@copaymentCode", "@encounterSequenceNumber", "@chiefComplaint", "@primaryDiagnosisCode", "@primaryDiagnosisName", "@secondaryDiagnosisCode", "@secondaryDiagnosisName", "@primaryProcedureCode", "@primaryProcedureName", "@secondaryProcedureCode", "@secondaryProcedureName", "@transferInBedNumber", "@wardFeeDiffSelfPay", "@orderId", "@orderDateTime", "@orderCategory", "@orderCode", "@orderName", "@dosage", "@dosageUnit", "@frequency", "@route", "@administrationMethod", "@days", "@totalQuantity", "@totalQuantityUnit", "@billingCategory", "@requisitionNumber", "@unitPrice", "@paymentPercentage", "@totalPrice", "@executionStartDateTime", "@executionEndDateTime", "@performer", "@doctor", "@nurse", "@familyMember", "@occupation", "@marriage", "@religion", "@admissionMethod", "@currentUser", "@institutionCode", "@institutionName", "@institutionAddress", "@institutionPhone", "@responsiblePerson", "@doctorSpecialty", "@doctorLicenseNumber"])),
  mrn: z.string(),
  encounterId: z.string().nullish(),
  keyword: z.string().nullish(),
  limit: z.number().nullish()
});
export type QueryRuntimeDataQuery = z.infer<typeof queryRuntimeDataQuerySchema>;

export const queryPreviousRuntimeFormQuerySchema = z.object({
  mrn: z.string(),
  templateId: z.string()
});
export type QueryPreviousRuntimeFormQuery = z.infer<typeof queryPreviousRuntimeFormQuerySchema>;

export const queryFormFieldSourceDataQuerySchema = z.object({
  templateId: z.string(),
  fieldId: z.string(),
  mrn: z.string()
});
export type QueryFormFieldSourceDataQuery = z.infer<typeof queryFormFieldSourceDataQuerySchema>;

export const downloadRuntimeFileDataQuerySchema = z.object({
  paramCode: z.array(z.enum(["eSignFileId"]))
});
export type DownloadRuntimeFileDataQuery = z.infer<typeof downloadRuntimeFileDataQuerySchema>;

export const queryQrcodeLinkIdQuerySchema = z.object({
  formId: z.string()
});
export type QueryQrcodeLinkIdQuery = z.infer<typeof queryQrcodeLinkIdQuerySchema>;

export const queryEncounterQuerySchema = z.object({
  mrn: z.string().nullish(),
  admissionDatetime: z.string().nullish(),
  dischargeDatetime: z.string().nullish(),
  stationId: z.string().nullish(),
  bedNo: z.string().nullish()
});
export type QueryEncounterQuery = z.infer<typeof queryEncounterQuerySchema>;

export const queryPatientEducationQuerySchema = z.object({
  encounterId: z.string()
});
export type QueryPatientEducationQuery = z.infer<typeof queryPatientEducationQuerySchema>;

export const queryQuerySchema = z.object({
  system: z.string(),
  tableName: z.string(),
  pageNum: z.number(),
  pageSize: z.number(),
  filters: z.string().nullish(),
  likes: z.string().nullish(),
  sorts: z.string().nullish()
});
export type QueryQuery = z.infer<typeof queryQuerySchema>;

export const queryStructureQuerySchema = z.object({
  system: z.string(),
  tableName: z.string()
});
export type QueryStructureQuery = z.infer<typeof queryStructureQuerySchema>;
